import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import {
  faCheck, faFile,
  faFileLines,
  faSearch,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import {
  ColumnMenuSettings, GridDataResult, PageChangeEvent, PagerPosition,
  PagerType,
  SelectionEvent
} from '@progress/kendo-angular-grid';
import { NotificationService } from '@progress/kendo-angular-notification';
import { process } from '@progress/kendo-data-query';
import { searchIcon, SVGIcon } from '@progress/kendo-svg-icons';
import moment from 'moment';
import { catchError, combineLatest, finalize, Observable, throwError } from 'rxjs';
import { DocumentService } from 'src/app/services/document.service';
import { LoadingService } from 'src/app/services/loading.service';
import { PdfAndImageService } from 'src/app/services/pdf-image.service';
import { dialogAnimationDuration, dialogTimer } from 'src/app/shared/constants/dialog-specs';
import { IReferenceData } from 'src/app/shared/interfaces/form/reference-data.model';
import { User } from 'src/app/shared/models/user';
import { AuthStore } from 'src/app/stores/auth.store';
import { ReferenceStore } from 'src/app/stores/reference.store';
import { ConsentRequestModel } from '../../models/consent-request-model';
import {
  ParentalConsentGrid
} from '../../models/parental-consent-grid-data-model';
import { AdminService } from '../../services/admin.service';
import { PageSize } from '../enums/page-sizes';
import { DocumentType } from '../../../../shared/enums/document-types';
import { DocumentCategory } from 'src/app/shared/enums/document-category';

@Component({
  selector: 'app-parental-consent-form',
  standalone: false,
  templateUrl: './parental-consent-form.component.html',
  styleUrl: './parental-consent-form.component.scss',
})
export class ParentalConsentFormComponent implements OnInit {
  public globalGridView: ParentalConsentGrid[];
  public globalData: ParentalConsentGrid[];
  public summaryGrid: ParentalConsentGrid[];
  public state = { skip: 0, take: 10 };
  public gridView: GridDataResult;
  faCheck = faCheck;
  public globalMySelection: string[] = [];
  public summarySelection: string[] = [];
  readonly phoneLength: number = 10;
  readonly areaCode: number = 3;
  readonly phoneNumberEnd: number = 6;
  public types: PagerType = 'numeric';
  readonly emptySelectionCount: number = 0;
  readonly buttonCount: number = 5;
  selectedItem: string[];
  loading: boolean = false;
  public info: boolean = true;
  public pageSizes: number[] = [
    PageSize.Minimum,
    PageSize.Few,
    PageSize.Some,
    PageSize.Many,
  ];
  public previousNext: boolean = true;
  public searchIcon: SVGIcon = searchIcon;
  showSummary: boolean = false;
  public position: PagerPosition = 'bottom';
  globalForm: FormGroup = new FormGroup({
    globalSearch: new FormControl(''),
  });
  public selectedRows: ParentalConsentGrid[] = [];
  user: User;
  consentDocument: any;
  languages: IReferenceData[] = [];
  menuSettings: ColumnMenuSettings = {
    view: 'list',
    columnChooser: false,
  };
  consentRecords: { [studentSk: string]: ParentalConsentGrid } = {};

  constructor(
    private adminService: AdminService,
    library: FaIconLibrary, private referenceStore: ReferenceStore,
    private notificationService: NotificationService,
    private authStore: AuthStore, private pdfAndImageService: PdfAndImageService,
    private loadingService: LoadingService,
    private documentService: DocumentService,
  ) {
    library.addIcons(faSearch, faFile, faFileLines, faCheck, faTimes);
    this.authStore.UserInfo$.subscribe((userInfo: User) => {
      this.user = userInfo;
    });
  }
  ngOnInit(): void {
    this.gridData();
    this.referenceStore.language$.subscribe((languages: IReferenceData[]) => {
      this.languages = languages;
    });
  }
  get referenceData$(): Observable<IReferenceData[][]> {
    return combineLatest([this.referenceStore.language$]);
  }
  gridData(): void {
    this.loading = true;

    const pageNumber = (this.state.skip / this.state.take) + 1;
    const pageSize = this.state.take;

    this.adminService
      .getMedicaidParentalConsentDetails(pageNumber, pageSize)
      .pipe(
        // if API call fails, throw error
        catchError((error: Error) =>
          throwError(() => new Error(error.message))
        ),
        finalize(() => this.loadingService.hide())
      )
      .subscribe({
      next: (response: any) => {
          // this.globalGridView = response.value;
          // this.globalGridView = this.globalGridView.map(student => ({
        const rows = response.value.map(student => ({
          ...student,
          studentFullName: `${student.studentFName} ${student.studentLName} ${student.studentNycId}`,
          formLastSent: student.formLastSent 
          ? moment(student.formLastSent).startOf('day').toDate() 
          : null,
          writtenLanguage: this.getLanguage(student?.writtenLanguage),
        }));
        this.globalData = rows;
        this.globalGridView = rows;
        this.gridView = {
          data: rows,
          total: rows.length ? rows[0].totalCount : 0,
        };
        this.loading = false;
      },
        error: (error: Error) => {
          // display notification that worklist was unable to be loaded
          this.notificationService.show({
            content: error.message,
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'fade', duration: 1200 },
            type: { style: 'error', icon: true },
          });
        },
      });
  }

  pageChange(event: PageChangeEvent): void {
    this.state.skip = event.skip;
    this.state.take = event.take;
    this.gridData();
  }

  onSelectionChange(event: SelectionEvent): void {
    event.selectedRows.forEach((row: { dataItem: ParentalConsentGrid }) => {
      if (
        !this.selectedRows.some(
          (x: ParentalConsentGrid) => x.studentSk === row.dataItem.studentSk
        )
      ) {
        this.selectedRows.push(row.dataItem);
      }
    });

    // Remove unselected rows
    event.deselectedRows.forEach((row: { dataItem: ParentalConsentGrid }) => {
      this.selectedRows = this.selectedRows.filter(
        (x: ParentalConsentGrid) => x.studentSk !== row.dataItem.studentSk
      );
    });
    this.selectedItem = this.globalMySelection;
  }
  saveConsentForm(): void {
    this.loadingService.show();
    if (!this.selectedRows.length) {
      return;
    }

    const payload: ConsentRequestModel[] = this.selectedRows.map(
      (row: ParentalConsentGrid) => new ConsentRequestModel(row, this.user)
    );
    const msgSpecific: string = `${this.selectedItem?.length} student(s) successfully had a Medical Parental Consent Form generated. `;
    this.adminService
      .addParentMedicalRecords(payload)
      .pipe(
        // if API call fails, throw error
        catchError((error: Error) =>
          throwError(() => new Error(error.message))
        ),
        finalize(() => this.loadingService.hide())
      )
      .subscribe({
        next: (response: any) => {
          this.showSummary = true;
          this.consentRecords = this.selectedRows.reduce((acc, row) => {
          const responseItem = response.value.find(
            (item: ParentalConsentGrid) => item.studentSk === row.studentSk
          );
          acc[row.studentSk] = {
            ...row,
            consentSk: responseItem?.consentSk,
          };
          return acc;
        }, {} as { [studentSk: string]: ParentalConsentGrid });

          const studentSkFromResponse = response.value.map(
            (item: ParentalConsentGrid) => item.studentSk
          );

          this.summaryGrid = this.selectedRows
            .filter((item: ParentalConsentGrid) =>
              studentSkFromResponse.includes(item.studentSk)
            )
            .map((item: ParentalConsentGrid) => ({
              studentSk: item.studentSk,
              studentFName: item.studentFName,
              studentLName: item.studentLName,
              parentFName: item.parentFName,
              parentLName: item.parentLName,
              studentNycId: item.studentNycId,
              parentMailAddress: item.parentMailAddress,
              writtenLanguage: this.getLanguage(item?.writtenLanguage) || item.writtenLanguage,
            }));


          this.loading = false;
          this.notificationService.show({
            content: msgSpecific,
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'fade', duration: 1200 },
            type: { style: 'success', icon: true },
            closable: true
          });
        },
        error: (error: Error) => {
          this.loadingService.hide();
          // display notification that worklist was unable to be loaded
          this.notificationService.show({
            content: error.message,
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'fade', duration: 1200 },
            type: { style: 'error', icon: true },
          });
        },
      });
  }
  formatPhoneNumber(phone: string): string {
    if (!phone) return '';
    const digits: string = phone.replace(/\D/g, '');
    if (digits.length === this.phoneLength) {
      return `(${digits.slice(0, this.areaCode)}) ${digits.slice(
        this.areaCode,
        this.phoneNumberEnd
      )}-${digits.slice(this.phoneNumberEnd)}`;
    }
    return phone;
  }
  public onGlobalFilter(inputValue: string): void {
    this.loadingService.show(); // display the loader
    if (inputValue) {
      const filtered = process(this.globalData, {
        filter: {
          logic: 'or',
          filters: [
            { 
              field: 'studentFName', 
              operator: 'contains', 
              value: inputValue 
            },
            { 
              field: 'studentLName', 
              operator: 'contains', 
              value: inputValue 
            },
            { 
              field: 'parentFName', 
              operator: 'contains', 
              value: inputValue 
            },
            { 
              field: 'studentNycId', 
              operator: 'contains', 
              value: inputValue 
            },
            { 
              field: 'parentMailAddress', 
              operator: 'contains', 
              value: inputValue 
            },
            { 
              field: 'parentLName', 
              operator: 'contains', 
              value: inputValue 
            },
          ],
        },
      }).data;

      this.gridView = {
        data: filtered,
        total: filtered.length,
      };
      this.loadingService.hide();
    } else {
      this.gridData(); // once API call finishes, loader is stopped
    }
  }
  returnToParent(): void {
    this.showSummary = false;
    // Clear selections
    this.selectedRows = [];
    this.globalMySelection = [];
    this.summarySelection = [];
    this.gridData();
  }
  printConsentForm(): void {
  this.summarySelection.forEach(studentSk => {
    const record: ParentalConsentGrid = this.consentRecords[studentSk];
    if (!record) {
      return;
    }

    this.documentService
      .createAndGetDoc(
        record.studentNycId,
        record.consentSk,
        DocumentCategory.ProviderManagement,
        DocumentType.MedicaidParentalConsent,
        record.studentSk
      )
      .subscribe({
        next: (response: Uint8Array) => {
          if (response) {
            this.pdfAndImageService.view(response, 'application/pdf');
          }
        },
        error: () => {
          this.notificationService.show({
            content: 'Failed to print form',
            hideAfter: dialogTimer,
            cssClass: 'notification-error',
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: dialogAnimationDuration },
            type: { style: 'error', icon: true },
          });
        }
      });
  });
}

  getLanguage(writtenLanguage: number | string): string {
    return (
      this.languages.find((l: IReferenceData) => l.id === Number(writtenLanguage))?.description ?? ''
    );
  }
  viewForm(document: number): void {
    this.documentService
      .getDocument(document)
      .subscribe({
        next: (response: Uint8Array) => {
          if (response) {
            this.pdfAndImageService.view(response, 'application/pdf');
          }
        },
        error: () => {
          this.notificationService.show({
            content: 'Failed to view form',
            hideAfter: dialogTimer,
            cssClass: 'notification-error',
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: dialogAnimationDuration },
            type: { style: 'error', icon: true },
          });
        }
      });
  }

}
